import { analyticsClick } from "../../../helpers/analytics";
import { isExternalLink } from "../../../helpers/isExternalLink";
import { OnSelectQuestionProps } from "../QuestionDetailModalRoot/QuestionDetailModalRoot";
import { RelatedQuestions } from "../RelatedQuestions/RelatedQuestions";
import { TagsList } from "../TagsList/TagsList";
import {
  CosmosButton,
  CosmosIconFeedbackFilled,
  CosmosIconWhatsapp,
  CosmosText,
  CosmosTitle,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./QuestionDetail.module.css";

const cx = classNames.bind(styles);

export function QuestionDetail({
  supportLink,
  question,
  fullAnswerButtonLink,
  topicNames,
  shortAnswer,
  customCtas,
  relatedQuestions,
  fullAnswerButtonLabel,
  onSelectQuestion,
  supportBannerCtaLabel,
  supportBannerHeadline,
  supportBannerBody,
}: {
  supportLink: string | undefined;
  question: string;
  fullAnswerButtonLink: string | undefined;
  topicNames: string[];
  shortAnswer: React.ReactNode;
  customCtas: {
    label: string;
    href: string | undefined;
    icon?: "whatsapp" | null;
  }[];
  relatedQuestions: {
    uuid: string;
    question: string;
    name?: string;
    topics: string[];
    href: string;
  }[];
  fullAnswerButtonLabel: string;
  supportBannerCtaLabel: string;
  supportBannerHeadline: string;
  supportBannerBody: string;
  onSelectQuestion: ({
    questionUuid,
    questionName,
    questionTopic,
  }: OnSelectQuestionProps) => void;
}) {
  return (
    <div className={cx("container")}>
      <div className={cx("question-wrapper")}>
        <div className={cx("content")}>
          <div className={cx("header")}>
            <TagsList className={cx("tags-list")} tags={topicNames} />
            <CosmosTitle
              tag="h2"
              weight="bold"
              size="small"
              className={cx("question-title")}
            >
              {question}
            </CosmosTitle>
          </div>
          <div className={cx("body")}>
            <div className={cx("prose")}>
              <CosmosText tag="p" className={cx("short-answer")} size="medium">
                {shortAnswer}
              </CosmosText>
            </div>
            {(fullAnswerButtonLink || customCtas.length > 0) && (
              <div className={cx("button-container")}>
                {fullAnswerButtonLink && (
                  <CosmosButton
                    type="button"
                    kind="primary"
                    size="medium"
                    href={fullAnswerButtonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      analyticsClick({
                        value: fullAnswerButtonLabel,
                        clickUrl: fullAnswerButtonLink ?? null,
                        elementPosition: "overlay window",
                        method: "Outbound: FALSE",
                        contentType: "CTA",
                      });
                    }}
                  >
                    {fullAnswerButtonLabel ?? "Learn More"}
                  </CosmosButton>
                )}
                {customCtas?.map((customCta, index) =>
                  customCta.href ? (
                    <CosmosButton
                      key={index}
                      type="button"
                      kind={
                        !fullAnswerButtonLink && index === 0
                          ? "primary"
                          : "secondary"
                      }
                      href={customCta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="medium"
                      iconPlacement={
                        customCta.icon === "whatsapp" ? "before" : undefined
                      }
                      onClick={() => {
                        analyticsClick({
                          value: customCta.label,
                          clickUrl: customCta.href ?? null,
                          elementPosition: "overlay window",
                          method: "Outbound: FALSE",
                          contentType: "CTA",
                        });
                      }}
                    >
                      {customCta.icon === "whatsapp" ? (
                        <CosmosIconWhatsapp slot="icon" aria-hidden="true" />
                      ) : null}
                      {customCta.label}
                    </CosmosButton>
                  ) : null,
                )}
              </div>
            )}
          </div>
        </div>
        {supportLink && (
          // TODO: Translate this
          <ContactCTA
            className={cx("contact-cta-container")}
            link={supportLink}
            title={supportBannerHeadline}
            body={supportBannerBody}
            buttonText={supportBannerCtaLabel}
          />
        )}
      </div>
      {relatedQuestions.length > 0 && (
        <div className={cx("relatedQuestions")}>
          <RelatedQuestions
            relatedQuestions={relatedQuestions}
            onSelectQuestion={onSelectQuestion}
          />
        </div>
      )}
    </div>
  );
}

function ContactCTA({
  className,
  link,
  title,
  body,
  buttonText,
}: {
  className?: string;
  link?: string;
  title: string;
  body: string;
  buttonText: string;
}) {
  return (
    <div className={cx("cta-container", className)}>
      <div className={cx("cta-header")}>
        <CosmosTitle
          tag="h2"
          weight="bold"
          size="small"
          className={cx("cta-title")}
        >
          {title}
        </CosmosTitle>
        <CosmosText kind="subtle" className={cx("cta-body")} size="x-small">
          {body}
        </CosmosText>
      </div>
      <CosmosButton
        kind="tertiary"
        size="medium"
        href={link}
        target={isExternalLink(link ?? "") ? "_blank" : undefined}
        rel={isExternalLink(link ?? "") ? "noopener noreferrer" : undefined}
        onClick={() => {
          analyticsClick({
            value: buttonText ?? "",
            clickUrl: link ?? null,
            elementPosition: "overlay window",
            method: `Outbound: ${isExternalLink(link ?? "") ? "TRUE" : "FALSE"}`,
            contentType: "CTA",
          });
        }}
      >
        <CosmosIconFeedbackFilled slot="icon" />
        {buttonText}
      </CosmosButton>
    </div>
  );
}
